import { useEffect, useRef, type ReactNode } from "react";
import { IonApp, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, IonLabel } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route, useHistory, useLocation } from "react-router-dom";

import { BadgeModal } from "./components/BadgeModal";
import { I } from "./components/icons";
import { LiveBar } from "./components/LiveBar";
import { Toast } from "./components/Shell";
import Today from "./pages/Today";
import Activity from "./pages/Activity";
import Library from "./pages/Library";
import Food from "./pages/Food";
import FoodPlan from "./pages/FoodPlan";
import FoodEnergy from "./pages/FoodEnergy";
import FoodWater from "./pages/FoodWater";
import FoodQuestions from "./pages/FoodQuestions";
import TrainQuestions from "./pages/TrainQuestions";
import TrainTests from "./pages/TrainTests";
import ProgressTrainingTests from "./pages/ProgressTrainingTests";
import ProgressBody from "./pages/ProgressBody";
import ProgressEnergy from "./pages/ProgressEnergy";
import ProgressFood from "./pages/ProgressFood";
import ProgressFoodWeek from "./pages/ProgressFoodWeek";
import ProgressMovement from "./pages/ProgressMovement";
import ProgressRecords from "./pages/ProgressRecords";
import ProgressTraining from "./pages/ProgressTraining";
import ProgressLevel from "./pages/ProgressLevel";
import ProgressCycle from "./pages/ProgressCycle";
import ProgressCycleDiary from "./pages/ProgressCycleDiary";
import ProgressCycleSymptom from "./pages/ProgressCycleSymptom";
import Login from "./pages/Login";
import Pending from "./pages/Pending";
import Train from "./pages/Train";
import TrainSession from "./pages/TrainSession";
import TrainYoga from "./pages/TrainYoga";
import TrainWeek from "./pages/TrainWeek";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminUsers from "./pages/admin/AdminUsers";
import Family from "./pages/family/Family";
import FamilyAnswers from "./pages/family/FamilyAnswers";
import FamilyExercise from "./pages/family/FamilyExercise";
import FamilyPerson from "./pages/family/FamilyPerson";
import FamilyPlan from "./pages/family/FamilyPlan";
import FamilyReminders from "./pages/family/FamilyReminders";
import FamilyTraining from "./pages/family/FamilyTraining";
import FamilyTrainingAnswers from "./pages/family/FamilyTrainingAnswers";
import FamilyWeek from "./pages/family/FamilyWeek";
import You from "./pages/You";
import AboutSettings from "./pages/settings/AboutSettings";
import AccountSettings from "./pages/settings/AccountSettings";
import DisplaySettings from "./pages/settings/DisplaySettings";
import AlertSettings from "./pages/settings/AlertSettings";
import BodyGoals from "./pages/settings/BodyGoals";
import CycleSettings from "./pages/settings/CycleSettings";
import FoodSettings from "./pages/settings/FoodSettings";
import TrainingSettings from "./pages/settings/TrainingSettings";
import SessionSettings from "./pages/settings/SessionSettings";
import StepCounting from "./pages/settings/StepCounting";
import TreadmillSettings from "./pages/settings/TreadmillSettings";
import WaterReminders from "./pages/settings/WaterReminders";
import Progress from "./pages/Progress";
import TreadRun from "./pages/TreadRun";
import Programs from "./pages/Programs";
import WalkRun from "./pages/WalkRun";
import TrainTreadmill from "./pages/TrainTreadmill";
import TrainWalks from "./pages/TrainWalks";
import Workout from "./pages/Workout";
import { todayStr } from "./domain/date";
import { sessionKeyFor } from "./domain/plan";
import { routePath } from "./domain/session";
import { dayFull } from "./domain/stats";
import { hasTreadmill } from "./domain/treadmill";
import { setAppBadge } from "./lib/device";
import { pushSeen } from "./lib/push";
import { scheduleReminders } from "./lib/reminders";
import { useKaya } from "./store/kayaStore";

/** Routes that take over the whole screen — the tab bar would only get in the way. */
// The questionnaire is a flow with its own Back and Next docked at the bottom edge (kit §28).
const FULL_SCREEN = ["/workout", "/treadrun", "/walk", "/login", "/food/questions", "/train/questions", "/train/tests"];

function TabBarVisibility() {
  const { pathname } = useLocation();
  // …and the same questionnaires in a linked person's name (ADR-046, ADR-056)
  const hide = FULL_SCREEN.some((p) => pathname.startsWith(p)) || /^\/you\/family\/[^/]+\/(answers|exercise)/.test(pathname);
  useEffect(() => {
    document.documentElement.classList.toggle("hide-tabs", hide);
  }, [hide]);
  return null;
}

/** Reminders are re-armed on every launch — see lib/reminders.ts for why that is needed. */
function useReminders() {
  const S = useKaya((s) => s.S);
  useEffect(() => {
    scheduleReminders(S);
  }, [S]);
}

/** How often a visible page tells the server it is on screen; under the server's three-minute
 *  window, so a page left open never misses the mark. */
const SEEN_MS = 2 * 60 * 1000;

/**
 * Tells Kaya's server this device has the app on screen, so it pushes nothing here meanwhile
 * (ADR-045): the page shows the day already, and a push the worker then has to hold back
 * (invariant 17) counts against the subscription on Safari.
 */
function usePushPresence() {
  useEffect(() => {
    const ping = () => {
      if (document.visibilityState === "visible") pushSeen();
    };
    ping();
    document.addEventListener("visibilitychange", ping);
    const t = window.setInterval(ping, SEEN_MS);
    return () => {
      document.removeEventListener("visibilitychange", ping);
      window.clearInterval(t);
    };
  }, []);
}

/** A dot on the installed app icon until today's session is done. */
function useAppBadge() {
  const S = useKaya((s) => s.S);
  useEffect(() => {
    const ds = todayStr();
    const scheduled = sessionKeyFor(S, ds) !== "R";
    setAppBadge(scheduled && !dayFull(S, ds) ? 1 : 0);
  }, [S]);
}

/** Where each PWA shortcut's `go` lands. `log=1` opens the weigh-in sheet, as "Log weight" says.
 *  The treadmill one goes through `/treadmill`, whose route picks the page: this runs before the
 *  state is hydrated, so it cannot know yet whether there is a treadmill. */
const SHORTCUT_TARGET: Record<string, string> = {
  today: "/today",
  treadmill: "/treadmill",
  progress: "/progress?log=1",
};

/** PWA shortcuts land on `/?go=treadmill` etc; translate that into a route once. */
function useLaunchShortcut() {
  useEffect(() => {
    const go = new URLSearchParams(location.search).get("go");
    if (!go) return;
    // A session in progress outranks the shortcut: `ResumeSession` below has already sent the
    // app there, and rewriting the URL underneath the router would leave the two disagreeing.
    if (useKaya.getState().session) return;
    const target = SHORTCUT_TARGET[go];
    if (!target) return;
    history.replaceState(null, "", target);
  }, []);
}

/**
 * With a session open, the app opens on it.
 *
 * The launcher opens the root, a shortcut opens its own tab, and a restored tab opens wherever
 * it was — none of them knew a walk was under way, so every one of them presented a Start button
 * over a session the phone's counter was still holding. Runs once, at launch; navigating away
 * from a session on purpose afterwards is allowed, and the banner takes over from there. (ADR-027)
 */
function ResumeSession() {
  const history = useHistory();
  const { pathname } = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const session = useKaya.getState().session;
    if (session && routePath(session.route) !== pathname) history.replace(session.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * The sign-in gate.
 *
 * Kaya is one person's server with a handful of accounts on it, so there is no signed-out mode any
 * more: without an approved account there is nothing to show, and the tab bar is hidden because
 * tabs that lead nowhere are worse than no tabs.
 *
 * It sits *inside* the router rather than around it, because both screens it renders use
 * `useHistory` and would throw outside one. And the account it reads comes from the local cache
 * before the network is consulted, so a launch in a tunnel opens the app rather than the sign-in
 * screen — the app is still offline-first, it just needs to know whose data it is holding.
 * (ADR-020)
 */
function Gate({ children }: { children: ReactNode }) {
  const user = useKaya((s) => s.user);
  const gated = !user || user.status !== "approved";

  // Only ever adds the class. Removing it is `TabBarVisibility`'s job, and its effect runs first
  // on the render that un-gates — clobbering it here would show the bar over the workout player.
  useEffect(() => {
    if (gated) document.documentElement.classList.add("hide-tabs");
  }, [gated]);

  if (!user) return <Login />;
  if (user.status !== "approved") return <Pending />;
  return <>{children}</>;
}

export default function App() {
  const ready = useKaya((s) => s.ready);
  useReminders();
  usePushPresence();
  useAppBadge();
  useLaunchShortcut();

  if (!ready) return <IonApp />;

  return (
    <IonApp>
      <IonReactRouter>
        <Gate>
          <TabBarVisibility />
          <ResumeSession />
          <IonTabs>
            <IonRouterOutlet>
              {/* Tab landings */}
              <Route exact path="/today" component={Today} />
              <Route exact path="/train" component={Train} />
              <Route exact path="/food" component={Food} />
              <Route exact path="/progress" component={Progress} />
              <Route exact path="/you" component={You} />

              {/* Focused pages, each under one tab */}
              <Route exact path="/today/activity" component={Activity} />
              <Route exact path="/train/session" component={TrainSession} />
              <Route exact path="/train/yoga" component={TrainYoga} />
              <Route exact path="/train/week" component={TrainWeek} />
              <Route exact path="/train/treadmill" component={TrainTreadmill} />
              <Route exact path="/train/walks" component={TrainWalks} />
              <Route exact path="/train/programs" component={Programs} />
              <Route exact path="/train/library" component={Library} />
              <Route exact path="/food/energy" component={FoodEnergy} />
              <Route exact path="/food/plan" component={FoodPlan} />
              <Route exact path="/food/water" component={FoodWater} />
              <Route exact path="/food/questions" component={FoodQuestions} />
              <Route exact path="/train/questions/:screen?" component={TrainQuestions} />
              <Route exact path="/train/tests/:test?" component={TrainTests} />
              <Route exact path="/progress/body" component={ProgressBody} />
              <Route exact path="/progress/energy" component={ProgressEnergy} />
              <Route exact path="/progress/food" component={ProgressFood} />
              <Route exact path="/progress/food/week" component={ProgressFoodWeek} />
              <Route exact path="/progress/training" component={ProgressTraining} />
              <Route exact path="/progress/training/tests" component={ProgressTrainingTests} />
              <Route exact path="/progress/movement" component={ProgressMovement} />
              <Route exact path="/progress/records/:series" component={ProgressRecords} />
              <Route exact path="/progress/cycle" component={ProgressCycle} />
              <Route exact path="/progress/cycle/diary" component={ProgressCycleDiary} />
              <Route exact path="/progress/cycle/diary/:row" component={ProgressCycleSymptom} />
              <Route exact path="/progress/level" component={ProgressLevel} />
              <Route exact path="/you/body" component={BodyGoals} />
              <Route exact path="/you/cycle" component={CycleSettings} />
              <Route exact path="/you/food" component={FoodSettings} />
              <Route exact path="/you/training" component={TrainingSettings} />
              <Route exact path="/you/session" component={SessionSettings} />
              <Route exact path="/you/treadmill" component={TreadmillSettings} />
              <Route exact path="/you/steps" component={StepCounting} />
              <Route exact path="/you/alerts" component={AlertSettings} />
              <Route exact path="/you/reminders/water" component={WaterReminders} />
              <Route exact path="/you/account" component={AccountSettings} />
              <Route exact path="/you/display" component={DisplaySettings} />
              <Route exact path="/you/about" component={AboutSettings} />
              <Route exact path="/you/people" component={AdminUsers} />
              <Route exact path="/you/people/:id" component={AdminUserDetail} />
              <Route exact path="/you/family" component={Family} />
              <Route exact path="/you/family/:id" component={FamilyPerson} />
              <Route exact path="/you/family/:id/plan" component={FamilyPlan} />
              <Route exact path="/you/family/:id/answers" component={FamilyAnswers} />
              <Route exact path="/you/family/:id/reminders" component={FamilyReminders} />
              <Route exact path="/you/family/:id/training" component={FamilyTraining} />
              <Route exact path="/you/family/:id/week" component={FamilyWeek} />
              <Route exact path="/you/family/:id/training-answers" component={FamilyTrainingAnswers} />
              <Route exact path="/you/family/:id/exercise/:screen?" component={FamilyExercise} />

              {/* Players. Their paths are persisted in an open session's record, so they never
                  move. (ADR-027) */}
              <Route exact path="/workout" component={Workout} />
              <Route exact path="/treadrun" component={TreadRun} />
              <Route exact path="/walk" component={WalkRun} />
              <Route exact path="/login" component={Login} />

              {/* The old addresses: saved links, installed shortcuts and docs/step-sync.md. */}
              <Redirect exact from="/" to="/today" />
              <Redirect exact from="/home" to="/today" />
              <Redirect exact from="/plan" to="/train" />
              {/* Without a treadmill, the treadmill page is a dead end; the walks are the same idea. */}
              <Route
                exact
                path="/treadmill"
                render={() => (
                  <Redirect to={hasTreadmill(useKaya.getState().S) ? "/train/treadmill" : "/train/walks"} />
                )}
              />
              <Redirect exact from="/library" to="/train/library" />
              <Redirect exact from="/programs" to="/train/programs" />
              <Redirect exact from="/levels" to="/progress/level" />
              <Redirect exact from="/progress/consistency" to="/progress/training" />
              <Redirect exact from="/profile" to="/you" />
              <Redirect exact from="/profile/:sub" to="/you/:sub" />
              <Redirect exact from="/admin/users" to="/you/people" />
              <Redirect exact from="/admin/users/:id" to="/you/people/:id" />

              {/* Anything else — a stale link, `/you/foo` from `/profile/foo` — would render an
                  empty outlet under the tab bar. Last, so every real route matches first. */}
              <Route render={() => <Redirect to="/today" />} />
            </IonRouterOutlet>

            {/* The open session, above the tab bar on every route but the players and Today.
                It sits in ion-tabs' bottom slot, so the outlet shrinks to make room. (ADR-030) */}
            <LiveBar hideUnder={FULL_SCREEN} hideAt={["/today"]} />

            {/* Five fixed tabs. A label never changes with a setting. (redesign plan, decision 1) */}
            <IonTabBar slot="bottom">
              <IonTabButton tab="today" href="/today">
                <span className="k-tabicon">{I.sun}</span>
                <IonLabel>Today</IonLabel>
              </IonTabButton>

              <IonTabButton tab="train" href="/train">
                <span className="k-tabicon">{I.dumbbell}</span>
                <IonLabel>Train</IonLabel>
              </IonTabButton>

              <IonTabButton tab="food" href="/food">
                <span className="k-tabicon">{I.bowl}</span>
                <IonLabel>Food</IonLabel>
              </IonTabButton>

              <IonTabButton tab="progress" href="/progress">
                <span className="k-tabicon">{I.progress}</span>
                <IonLabel>Progress</IonLabel>
              </IonTabButton>

              <IonTabButton tab="you" href="/you">
                <span className="k-tabicon">{I.user}</span>
                <IonLabel>You</IonLabel>
              </IonTabButton>
            </IonTabBar>
          </IonTabs>
        </Gate>

        <Toast />
        <BadgeModal />
      </IonReactRouter>
    </IonApp>
  );
}
